import React, { useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { Button } from './button'
import { cn } from './cn'
import { CountBadge } from './count-badge'

export interface FilterField {
  id: string
  label: string
  width?: 'sm' | 'md' | 'lg' | 'full'
  advanced?: boolean
  render: () => React.ReactNode
}

export interface AppliedFilter {
  id: string
  label: string
  onRemove: () => void
}

export interface FilterBarProps {
  fields: FilterField[]
  applied: AppliedFilter[]
  onReset: () => void
  children?: React.ReactNode
  className?: string
  advancedLabel?: string
  resetLabel?: string
}

const widthClass = {
  sm: 'w-[var(--fw-sm)]',
  md: 'w-[var(--fw-md)]',
  lg: 'w-[var(--fw-lg)] max-w-full',
  full: 'w-full',
} as const

/**
 * Wrapping filter layout: each field keeps the width it declares instead of
 * stretching to fill a row. Applied chips are the only place values repeat.
 */
export function FilterBar({
  fields,
  applied,
  onReset,
  children,
  className,
  advancedLabel = 'More filters',
  resetLabel = 'Reset filters',
}: FilterBarProps) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const primary = fields.filter((field) => !field.advanced)
  const advanced = fields.filter((field) => field.advanced)
  const renderField = (field: FilterField) => (
    <div
      key={field.id}
      className={cn('flex min-w-0 flex-col gap-1.5', widthClass[field.width ?? 'md'])}
    >
      <span className="text-xs text-muted">{field.label}</span>
      {field.render()}
    </div>
  )

  return (
    <section
      className={cn(
        'flex flex-col gap-3 rounded-[var(--radius)] border border-border bg-background p-3',
        className
      )}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div
          data-testid="filter-grid"
          className="flex min-w-0 flex-1 flex-wrap items-end justify-start gap-3"
        >
          {primary.map(renderField)}
        </div>
        {children ? (
          <div className="ml-auto flex shrink-0 items-center gap-2">{children}</div>
        ) : null}
      </div>

      {advanced.length > 0 ? (
        <>
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="px-2 py-1 text-xs"
              aria-expanded={showAdvanced}
              onClick={() => setShowAdvanced((show) => !show)}
            >
              <ChevronDown
                aria-hidden="true"
                className={cn('size-3.5 transition-transform', showAdvanced && 'rotate-180')}
              />
              {advancedLabel}
              {applied.filter((filter) => advanced.some((field) => field.id === filter.id)).length >
              0 ? (
                <CountBadge
                  count={
                    applied.filter((filter) => advanced.some((field) => field.id === filter.id))
                      .length
                  }
                />
              ) : null}
            </Button>
          </div>
          {showAdvanced ? (
            <div className="flex flex-wrap items-end justify-start gap-3 border-t border-border pt-3">
              {advanced.map(renderField)}
            </div>
          ) : null}
        </>
      ) : null}

      {applied.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <span className="text-xs text-muted">Applied</span>
          {applied.map((filter) => (
            <span
              key={filter.id}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-surface py-1 pl-2 pr-1 text-xs text-foreground"
            >
              {filter.label}
              <button
                type="button"
                className="grid size-5 place-items-center rounded-full text-muted hover:bg-background hover:text-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)]"
                aria-label={`Remove ${filter.label}`}
                onClick={filter.onRemove}
              >
                <X aria-hidden="true" className="size-3" />
              </button>
            </span>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto px-2 py-1 text-xs"
            onClick={onReset}
          >
            {resetLabel}
          </Button>
        </div>
      ) : null}
    </section>
  )
}
